import { Popover } from '@mui/material';
import type { User } from '@takeshape/schema';
import type { updateUser } from 'app/auth/actions';
import useMenu from 'app/common/hooks/useMenu';
import type { trackEvent } from 'app/user-tracking/actions';
import type React from 'react';
import { useCallback, useEffect } from 'react';
import styled from 'styled-components';
import HelpButton from '../help-button';
import HelpMenu from '../help-menu';

const HelpContainer = styled('div')`
  z-index: ${({ theme }) => theme.zIndex.modal};
`;

export type HelpProps = {
  readonly projectId: string;
  readonly user: User | null;
  readonly trackEvent: typeof trackEvent;
  readonly updateUser: typeof updateUser;
};

/** Provides a menu of resources to help TakeShape users understand the application.
 *  It also determines when to show a `<Welcome Tour>` component that introduces new
 *  users to the fundamental capabilities and use cases of TakeShape.
 */
export const Help: React.FC<HelpProps> = (props) => {
  const { projectId, user, trackEvent, updateUser } = props;
  const { menuRef, openMenu, closeMenu } = useMenu();

  const handleHelpToggle = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (!menuRef) {
        trackEvent('Opened the Help Menu');
        openMenu(event);
      }
    },
    [menuRef, openMenu, trackEvent]
  );

  useEffect(() => {
    if (user?.firstTime && projectId) {
      updateUser({ firstTime: false }, true);
    }
  }, [user, projectId, updateUser]);

  return (
    <HelpContainer>
      <HelpButton onToggle={handleHelpToggle} helpActive={Boolean(menuRef)} />
      <Popover
        open={Boolean(menuRef)}
        anchorEl={menuRef}
        onClose={closeMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <HelpMenu loggedIn={Boolean(user)} inProject={Boolean(projectId)} trackEvent={trackEvent} />
      </Popover>
    </HelpContainer>
  );
};

export default Help;
